/**
 * bulkRefreshStore — drives the floating progress pill for the
 * "Refresh metadata across N projects" runner. The runner is fire-and-forget
 * once kicked off from Settings, so its state has to outlive the modal —
 * Settings can be closed mid-run, the user can keep working, and the pill
 * + completion toast surface the outcome.
 */
import { create } from 'zustand';
import type { RefreshFields } from '@/lib/projectAIRefresh';

interface BulkRefreshState {
  status: 'idle' | 'running' | 'done';
  total: number;
  done: number;
  /** Project currently being processed (null while idle / between projects). */
  currentName: string | null;
  /** Snapshot of the field set the run was kicked off with — drives the
   *  Settings UI when the user reopens it mid-run. */
  fields: RefreshFields | null;
  /** Aggregate counters, populated on finish(). */
  succeeded: number;
  skipped: number;
  failed: number;
  startedAt: number | null;
  finishedAt: number | null;

  start: (total: number, firstName: string, fields: RefreshFields) => void;
  updateProgress: (done: number, currentName: string) => void;
  finish: (succeeded: number, skipped: number, failed: number) => void;
  /** Clear the "done" banner once the user has acknowledged it. */
  dismiss: () => void;
}

export const useBulkRefreshStore = create<BulkRefreshState>((set) => ({
  status: 'idle',
  total: 0,
  done: 0,
  currentName: null,
  fields: null,
  succeeded: 0,
  skipped: 0,
  failed: 0,
  startedAt: null,
  finishedAt: null,

  start: (total, firstName, fields) =>
    set({
      status: 'running',
      total,
      done: 0,
      currentName: firstName,
      fields,
      succeeded: 0,
      skipped: 0,
      failed: 0,
      startedAt: Date.now(),
      finishedAt: null,
    }),

  updateProgress: (done, currentName) =>
    set({ done, currentName }),

  finish: (succeeded, skipped, failed) =>
    set({
      status: 'done',
      succeeded,
      skipped,
      failed,
      currentName: null,
      finishedAt: Date.now(),
    }),

  dismiss: () =>
    set({
      status: 'idle',
      total: 0,
      done: 0,
      currentName: null,
      fields: null,
      succeeded: 0,
      skipped: 0,
      failed: 0,
      startedAt: null,
      finishedAt: null,
    }),
}));
